import {
  COMPLETION_ALERT,
  REVIEW_COMPLETION_CHANGE,
  type CompletionAlert,
  acknowledgeReviewCompletions,
  completionKey,
  watchedReviewCompletions,
} from "@/entities/notification/completion-alerts"
import {
  checkWatchedReviewCompletions,
  notifyReviewCompletion,
  syncReviewBrowserNotifications,
} from "@/entities/notification/review-completion-monitor"
import { reviewKeys } from "@/entities/review/review.queries"
import {
  acknowledgeCompletionInbox,
  completionInboxEntries,
  recordCompletionNotification,
} from "@/entities/notification/completion-inbox"
import { CompletionTaskName } from "@/features/notification/completion-task-name"
import { completionTaskName, completionTaskQuery } from "@/features/notification/completion-task.lib"
import { TranslationQueryKeysEnum } from "@/entities/translation/translation.constants"
import toast, { type Toast } from "react-hot-toast"
import {
  checkWatchedAssistantCompletions,
  clearAssistantCompletionFavicon,
  conversationAtPath,
  notifyAssistantCompletion,
  syncAssistantBrowserNotifications,
  syncAssistantCompletionFavicon,
} from "@/entities/assistant/assistant.completion-monitor"
import {
  ASSISTANT_COMPLETION_CHANGE,
  acknowledgeAssistantConversation,
  unreadAssistantCompletions,
  watchedAssistantRequests,
} from "@/entities/assistant/assistant.completion-notifications"
import { assistantKeys } from "@/entities/assistant/assistant.queries"
import { useAuthStore } from "@/entities/auth/auth.model"
import { getAccessToken } from "@/shared/lib/token/token"
import { useQueryClient } from "@tanstack/react-query"
import { useLocation, useNavigate } from "@tanstack/react-router"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

const POLL_MS = 10000

/** Lives above every route so leaving the chat does not stop completion polling. */
export function AssistantCompletionMonitor() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const accountId = useAuthStore((state) => state.user.id)
  const pathname = useLocation({ select: (location) => location.pathname })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  useEffect(() => {
    const syncAlerts = () => {
      syncAssistantCompletionFavicon()
      syncAssistantBrowserNotifications()
      syncReviewBrowserNotifications()
    }
    window.addEventListener(ASSISTANT_COMPLETION_CHANGE, syncAlerts)
    window.addEventListener(REVIEW_COMPLETION_CHANGE, syncAlerts)
    syncAlerts()
    return () => {
      window.removeEventListener(ASSISTANT_COMPLETION_CHANGE, syncAlerts)
      window.removeEventListener(REVIEW_COMPLETION_CHANGE, syncAlerts)
      clearAssistantCompletionFavicon()
    }
  }, [])

  useEffect(() => {
    const acknowledgeVisibleConversation = () => {
      if (document.visibilityState === "hidden" || !document.hasFocus()) return
      const conversationId = conversationAtPath(pathname)
      if (conversationId) acknowledgeAssistantConversation(conversationId)
      acknowledgeReviewCompletions(pathname)
      acknowledgeCompletionInbox(pathname)
    }
    acknowledgeVisibleConversation()
    window.addEventListener("focus", acknowledgeVisibleConversation)
    document.addEventListener(
      "visibilitychange",
      acknowledgeVisibleConversation
    )
    return () => {
      window.removeEventListener("focus", acknowledgeVisibleConversation)
      document.removeEventListener(
        "visibilitychange",
        acknowledgeVisibleConversation
      )
    }
  }, [pathname])

  useEffect(() => {
    if (!isAuthenticated) return
    let disposed = false
    const toastIds = new Set<string>()
    const titleTimers = new Set<number>()
    const announce = (event: Event) => {
      const entry = (event as CustomEvent<CompletionAlert>).detail
      recordCompletionNotification(entry)
      const title = t(
        entry.kind === "cat-review"
          ? "mt.reviewCompletionTitle"
          : "assistant.completionNotificationTitle"
      )
      const body = t(
        entry.kind === "cat-review"
          ? "mt.reviewCompletionBody"
          : entry.kind === "review"
            ? "review.completionNotificationBody"
            : "assistant.completionNotificationBody"
      )
      const open = () => {
        window.focus()
        if (entry.kind === "assistant") {
          acknowledgeAssistantConversation(entry.resourceId)
          void navigate({
            to: "/assistant/$conversationId",
            params: { conversationId: entry.resourceId },
          })
        } else if (entry.kind === "review") {
          void navigate({
            to: "/reviews/$reviewId",
            params: { reviewId: entry.resourceId },
          })
        } else {
          void navigate({
            to: "/translations/jobs/$jobId/editor",
            params: { jobId: entry.resourceId },
          })
        }
      }
      // This also confirms completion to a reader on the current screen, and works without
      // browser permission or OS notification support.
      const toastId = toast(
        (notice: Toast) => (
          <div className="flex min-w-0 items-center gap-3 text-xs leading-5">
            <div className="min-w-0" role="status">
              <div>{title}</div>
              {entry.kind === "assistant" && (
                <CompletionTaskName conversationId={entry.resourceId} />
              )}
            </div>
            <button
              type="button"
              className="shrink-0 text-key-color underline"
              onClick={() => {
                toast.dismiss(notice.id)
                open()
              }}
            >
              {t("assistant.completionOpenResult")}
            </button>
          </div>
        ),
        { id: completionKey(entry), duration: 8000 }
      )
      toastIds.add(toastId)
      if (entry.kind === "assistant") {
        void queryClient.invalidateQueries({
          queryKey: assistantKeys.conversations(),
        })
        let notified = false
        const notify = (name: string) => {
          // Logout or deletion may win while the title request is in flight.
          if (
            disposed || notified ||
            !completionInboxEntries().some(
              (item) => completionKey(item) === completionKey(entry) && !item.isRead
            ) ||
            !unreadAssistantCompletions().some(
              (item) => item.conversationId === entry.resourceId && item.requestId === entry.runId
            )
          ) return
          notified = true
          notifyAssistantCompletion(entry.resourceId, title, `${name}\n${body}`, open)
        }
        // Completion is already known. A stalled name lookup must never withhold its alert.
        const fallbackName = completionTaskName(entry.resourceId)
        const timer = window.setTimeout(() => {
          titleTimers.delete(timer)
          notify(fallbackName)
        }, 2000)
        titleTimers.add(timer)
        void queryClient.fetchQuery(completionTaskQuery(entry.resourceId, queryClient)).then(
          (name) => notify(name),
          () => notify(fallbackName)
        ).finally(() => {
          window.clearTimeout(timer)
          titleTimers.delete(timer)
        })
      } else {
        if (entry.kind === "review") {
          void queryClient.invalidateQueries({ queryKey: reviewKeys.all() })
        } else {
          void queryClient.invalidateQueries({
            queryKey: [
              TranslationQueryKeysEnum.FetchCatReviewLatestRun,
              entry.resourceId,
            ],
          })
        }
        notifyReviewCompletion(entry, title, body, open)
      }
    }
    window.addEventListener(COMPLETION_ALERT, announce)
    return () => {
      disposed = true
      titleTimers.forEach((timer) => window.clearTimeout(timer))
      toastIds.forEach((id) => toast.remove(id))
      window.removeEventListener(COMPLETION_ALERT, announce)
    }
  }, [accountId, isAuthenticated, navigate, queryClient, t])

  useEffect(() => {
    if (!isAuthenticated) return
    let polling = false
    let disposed = false
    let timer: number | undefined

    const schedule = () => {
      if (
        !disposed &&
        (watchedAssistantRequests().length || watchedReviewCompletions().length)
      ) {
        timer = window.setTimeout(() => void poll(), POLL_MS)
      }
    }

    const poll = async () => {
      if (polling || disposed) return
      polling = true
      try {
        if (getAccessToken()) {
          await Promise.all([
            checkWatchedAssistantCompletions(() => {}),
            checkWatchedReviewCompletions(),
          ])
        }
      } finally {
        polling = false
        schedule()
      }
    }

    const pollNow = () => {
      window.clearTimeout(timer)
      timer = undefined
      void poll()
    }
    pollNow()
    window.addEventListener(ASSISTANT_COMPLETION_CHANGE, pollNow)
    window.addEventListener(REVIEW_COMPLETION_CHANGE, pollNow)
    window.addEventListener("focus", pollNow)
    document.addEventListener("visibilitychange", pollNow)
    return () => {
      disposed = true
      window.clearTimeout(timer)
      window.removeEventListener(ASSISTANT_COMPLETION_CHANGE, pollNow)
      window.removeEventListener(REVIEW_COMPLETION_CHANGE, pollNow)
      window.removeEventListener("focus", pollNow)
      document.removeEventListener("visibilitychange", pollNow)
    }
  }, [isAuthenticated, navigate, queryClient, t])

  return null
}
